import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, HoverCard } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import type { EditorRoleRow } from './types';
import { RolePickerPopover } from './RolePickerPopover';

/**
 * EAA-1884: `RoleEditorTable`'s "Vai trò chuẩn" column cell — the standard
 * role's identity plus its level/link meta. Split out of the old hand-rolled
 * `<tr>`/`<td>` `RoleEditorRow` (now two `DataTable` `ColumnConfig.render`
 * cells) so the table itself can be the shared kit `DataTable` instead of a
 * raw `<table>`.
 */
export function RoleEditorIdentityCell({ row }: { row: EditorRoleRow }) {
    const { t } = useTranslation();
    return (
        <>
            <span className="rc-role-key">{row.roleKey}</span>
            <p className="rc-card-note">
                {t('console.roleCoverage.editorRow.roleMeta', {
                    roleName: row.roleName,
                    levelCount: row.levelCount,
                    linkCount: row.linkCount,
                })}
            </p>
        </>
    );
}

export interface RoleEditorChipsCellProps {
    row: EditorRoleRow;
    appName: string;
    canManage: boolean;
    appRoleOptions: AppRoleOption[];
    appRolesLoading: boolean;
    isPickerOpen: boolean;
    onOpenPicker: () => void;
    onClosePicker: () => void;
    onAddRole: (appRole: string) => void;
    onRemoveChip: (chip: { id: string; appRole: string }) => void;
    removePending: boolean;
}

/**
 * EAA-1884: `RoleEditorTable`'s "Vai trò trong {app}" column cell — the
 * row's N removable chips plus the "+ vai trò" trigger/popover. Kept as its
 * own component (not inlined in a `ColumnConfig.render` callback) because it
 * owns hooks: the `addButtonRef`/`wasPickerOpen` focus-restore effect below
 * needs a real component instance per row — `render` is a plain function, so
 * calling hooks directly inside it would break the rules of hooks, but
 * returning `<RoleEditorChipsCell />` from it mounts a normal component that
 * `DataTable` renders once per row exactly like before.
 */
export function RoleEditorChipsCell({
    row,
    appName,
    canManage,
    appRoleOptions,
    appRolesLoading,
    isPickerOpen,
    onOpenPicker,
    onClosePicker,
    onAddRole,
    onRemoveChip,
    removePending,
}: RoleEditorChipsCellProps) {
    const { t } = useTranslation();
    const alreadyLinked = row.chips.map((chip) => chip.appRole);

    // EAA-1817: the popover unmounts on close (Escape / outside click / a
    // successful pick) with no focus management of its own — restore focus
    // to the trigger that opened it so a keyboard user isn't dropped back to
    // the top of the document. The kit `Chip` renders a plain `<span>` and
    // does not forward a ref, so the ref sits on a wrapping span instead and
    // the effect below focuses the chip's own `role="button"` element.
    const addButtonRef = useRef<HTMLSpanElement>(null);
    const wasPickerOpen = useRef(isPickerOpen);
    useEffect(() => {
        if (wasPickerOpen.current && !isPickerOpen) {
            addButtonRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
        }
        wasPickerOpen.current = isPickerOpen;
    }, [isPickerOpen]);

    return (
        <div className="portal-console-chips">
            {/* EAA-1817: was a hand-rolled pill-shaped chip (`.portal-console-chip`)
                that visually disagreed with `RoleMapTable`'s kit `Chip`
                (rounded-rect, monospace, inset ring) for the exact same
                role↔app_role concept, one screen-switch away — now identical. */}
            {row.chips.map((chip) => {
                const removeLabel = t('console.roleCoverage.editorRow.removeChipAriaLabel', {
                    appRole: chip.appRole,
                    roleKey: row.roleKey,
                });
                // EAA-1817: `Chip` has no `disabled` affordance for `onRemove`
                // (unlike the old `.rc-chip-remove` button) — guard inside the
                // handler so a pending removal still can't be double-fired.
                const chipEl = (
                    <Chip
                        tone={chip.isStale ? 'stale' : 'mapped'}
                        removeLabel={removeLabel}
                        onRemove={
                            canManage
                                ? () => {
                                      if (removePending) return;
                                      onRemoveChip({ id: chip.id, appRole: chip.appRole });
                                  }
                                : undefined
                        }
                    >
                        {chip.appRole}
                    </Chip>
                );

                if (!chip.isStale) {
                    return <span key={chip.id}>{chipEl}</span>;
                }

                // EAA-1817: swap the native `title` tooltip for the kit
                // `HoverCard` — same explanatory text, themed overlay.
                return (
                    <HoverCard
                        key={chip.id}
                        trigger={chipEl}
                        content={t('console.roleCoverage.editorRow.staleTooltip')}
                    />
                );
            })}

            {canManage && (
                <span className="rc-role-picker-anchor">
                    <span ref={addButtonRef}>
                        <Chip tone="add" onClick={onOpenPicker}>
                            {t('console.roleCoverage.editorRow.addRoleButton')}
                        </Chip>
                    </span>
                    {isPickerOpen && (
                        <RolePickerPopover
                            appName={appName}
                            roleKey={row.roleKey}
                            options={appRoleOptions}
                            loading={appRolesLoading}
                            alreadyLinked={alreadyLinked}
                            onPick={onAddRole}
                            onClose={onClosePicker}
                        />
                    )}
                </span>
            )}
        </div>
    );
}
